// src/oura/components/site/sitePages.ts
//
// The pages that matter, as the Site screen lists them: the backend's
// priority pages (ranked by Search Console clicks when it is connected, by
// internal links otherwise), each with the crawl findings on it and the role
// a reviewer gave it.

import type { CrawledPageRow, PriorityPage } from "../../audit";
import { pageUrlKey } from "../../lib/oura";
import { roleForUrl, type PageRole } from "../../pageWorkshop";
import { SEVERITY_ORDER, type ScopedIssue } from "./siteIssues";
import { roleWord } from "./siteCopy";

export interface SitePageRow {
    url: string;
    title: string;
    clicks: number;
    /** Findings on this page. `null` when the crawl inventory does not hold
     *  the page, which is not the same as a page that passed everything. */
    issues: number | null;
    /** Role, then the worst finding (or the backend's note when none). */
    line: string;
}

export function sitePageRows({
    priority,
    crawled,
    issues,
    roles,
    setAside,
}: {
    priority: PriorityPage[];
    crawled?: CrawledPageRow[];
    /** Every scoped finding, so a page's failed keys resolve to labels. */
    issues: ScopedIssue[];
    roles: Map<string, PageRole>;
    setAside: Set<string>;
}): SitePageRow[] {
    const byKey = new Map(issues.map((i) => [i.key, i]));
    const inventory = crawled ? new Map(crawled.map((p) => [pageUrlKey(p.url), p.failed])) : null;
    // A note that is the same on every row describes the list, not the page
    // ("Most internally-linked pages"), so it is never repeated per row.
    const noteIsListLevel = priority.length > 1 && new Set(priority.map((p) => p.note)).size === 1;
    // Search Console backs `visits`. All-zero across every page means the
    // connection is missing, not that the pages are unread: the BE sends 0
    // per page rather than omitting them.
    const ranked = priority.some((p) => p.visits > 0)
        ? [...priority].sort((a, b) => b.visits - a.visits)
        : priority;

    return ranked
        // The same scoping as the findings: a page a reviewer set aside is
        // counted in the footer, not listed as a page to work on.
        .filter((p) => !setAside.has(pageUrlKey(p.url)))
        .map((p) => {
            const key = pageUrlKey(p.url);
            // The crawl inventory is the whole truth per page (#3957). Without
            // it (fixtures, older scans), the findings' own page lists are the
            // next best source, and they are the same crawl.
            const failed = inventory
                ? inventory.get(key) ?? null
                : issues.filter((i) => i.count > 0 && i.pages.some((pg) => pageUrlKey(pg.url) === key)).map((i) => i.key);
            const worst = failed
                ?.map((k) => byKey.get(k))
                .filter((i): i is ScopedIssue => Boolean(i))
                .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))[0];
            const role = roleWord(roles.get(key) ?? roleForUrl(p.url));
            const why = worst?.label ?? (noteIsListLevel ? null : p.note || null);
            return {
                url: p.url,
                title: p.title || p.url,
                clicks: p.visits,
                issues: failed ? failed.length : null,
                line: why ? `${role} · ${why}` : role,
            };
        });
}
